import React from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import propagandaData from '../../static/propaganda-data';
import BaseInput from '../../component/form/base-input.component';
import DialogComponent from '../../component/dialog/dialog.component';
import DialogFormContentSurface, { getDialogFormBodyContentSx } from '../../component/dialog/dialog-form-content-surface.component';
import GlobalNotification from '../../component/common/snackbar';
import { apiManage_rbp } from '../../request';
import { Propaganda } from '../../models';
import _ from 'lodash';
import i18n from '../../i18n';
import { CardToolbarIconButton } from '../../component/button/card-toolbar-icon-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';

/**
 * 社媒宣传链接：Figma 1838:52035 为纵向条列表（填充底条 + 左图标/URL + 右操作），新增/编辑走弹窗。
 */
export default class PropagandaComponent extends React.Component<Props, State> {
    /** 链接列表与弹窗状态 */
    public state: Readonly<State> = {
        propaganda: [],
        open: false,
        /** 弹窗「确定」提交中 */
        saveLoading: false,
        /** 删除社媒链接二次确认弹窗是否打开 */
        deleteConfirmOpen: false,
        /** 待删除行的 id */
        deleteTargetId: null,
        /** 待删除项 URL 摘要，弹窗高亮行 */
        deleteHighlight: '',
        /** 删除请求中，确认按钮 loading */
        deleteLoading: false,
    };

    async componentDidMount() {
        await this.init();
    }

    async init() {
        await this.getPropaganda();
    }

    async getPropaganda() {
        const propaganda = await apiManage_rbp.get_propaganda();
        if (!_.get(propaganda, 'error')) {
            this.setState({
                propaganda,
            });
        }
    }

    /** 弹窗内保存：校验通过后请求接口，保存按钮使用公共 LoadingOverlayButton */
    async savePropaganda() {
        const { propagandaDetail = {} } = this.state;
        const regex = /(https?:\/\/)+/g;
        if (!(propagandaDetail.name && _.trim(`${propagandaDetail.url}`))) {
            GlobalNotification.error(i18n.t('dis_setting_page_type_entire_info'));
            return;
        }
        if (!regex.test(`${propagandaDetail.url}`)) {
            GlobalNotification.error(i18n.t('first_screen_setting_page_error_web_address_enter_true'));
            return;
        }
        this.setState({ saveLoading: true });
        try {
            let result;
            if (typeof propagandaDetail.id === 'undefined') {
                result = await apiManage_rbp.post_propaganda(propagandaDetail);
            } else {
                const { id, ...otherParams } = propagandaDetail;
                result = await apiManage_rbp.patch_propaganda(id, otherParams);
            }

            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    open: false,
                });
                void this.getPropaganda();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ saveLoading: false });
        }
    }

    /**
     * 关闭删除确认弹窗（请求中不可关，与 AlertDialog confirmLoading 一致）。
     */
    private closeDeleteConfirm = (): void => {
        if (this.state.deleteLoading) {
            return;
        }
        this.setState({
            deleteConfirmOpen: false,
            deleteTargetId: null,
            deleteHighlight: '',
        });
    };

    /**
     * 确认后删除社媒宣传链接；接口可能较慢，期间保持确认按钮 loading。
     */
    private handleConfirmDeletePropaganda = async (): Promise<void> => {
        const { deleteTargetId } = this.state;
        if (deleteTargetId == null) {
            return;
        }
        this.setState({ deleteLoading: true });
        try {
            const result = await apiManage_rbp.delete_propaganda(deleteTargetId);
            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    deleteConfirmOpen: false,
                    deleteTargetId: null,
                    deleteHighlight: '',
                    propagandaDetail: undefined,
                });
                void this.getPropaganda();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ deleteLoading: false });
        }
    };

    onChangePropaganda(value: string, key: string) {
        const { propagandaDetail = {} } = this.state;
        _.set(propagandaDetail, key, value);
        this.setState({
            propagandaDetail,
        });
    }

    render() {
        const { propaganda, propagandaDetail, open, saveLoading, deleteConfirmOpen, deleteHighlight, deleteLoading } =
            this.state;

        const propagandaForm = [
            {
                key: 'name',
                label: i18n.t('global_text_name'),
                regularType: 'required',
                require: true,
                disabled: typeof propagandaDetail?.id !== 'undefined',
                select: true,
                items: propagandaData.map((item) => {
                    const disabled = propaganda.findIndex((dataItem) => dataItem.name === item.key) > -1;
                    return {
                        label: item.label,
                        value: item.key,
                        disabled,
                    };
                }),
            },
            {
                key: 'url',
                label: i18n.t('first_screen_setting_page_web_address'),
                regularType: 'required',
                require: true,
            },
        ];

        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;

        return (
            <Box
                className="propaganda-social-list"
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    flex: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%',
                        background: 'var(--fill-neutral-100)',
                        borderRadius: '16px',
                        padding: '16px',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)', fontWeight: 700 }}>
                            {i18n.t('first_screen_setting_page_advertise_link')}
                        </Typography>
                        <CardToolbarIconButton
                            kind="add"
                            disabled={propaganda.length === 10}
                            ariaLabel={i18n.t('general_text_add')}
                            onClick={() => {
                                this.setState({
                                    propagandaDetail: {},
                                    open: true,
                                    saveLoading: false,
                                });
                            }}
                        />
                    </Box>
                    <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey="figma_aux_project_url_example" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, width: '100%' }}>
                        {propaganda.length === 0 ? (
                            <Box
                                sx={{
                                    flex: '1 1 0',
                                    minHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: 'var(--text-primary-500)' }}>
                                    {i18n.t('google_text_public_text_no_data')}
                                </Typography>
                            </Box>
                        ) : (
                            propaganda.map((row, dataIndex) => {
                                const meta = propagandaData.find((item) => item.key === row.name);
                                const urlText = row.url ?? '';
                                return (
                                    <Box
                                        key={row.id ?? dataIndex}
                                        className="propaganda-social-list__row"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            padding: '8px',
                                            borderRadius: '8px',
                                            background: 'var(--fill-neutral-200)',
                                        }}
                                    >
                                        <Box sx={{ flexShrink: 0 }}>
                                            {meta?.icon ? (
                                                <Avatar
                                                    src={meta.icon}
                                                    variant="circular"
                                                    sx={{ width: 48, height: 48, bgcolor: 'var(--fill-neutral-100)' }}
                                                    imgProps={{ style: { objectFit: 'contain' } }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: '50%',
                                                        bgcolor: 'var(--fill-neutral-100)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        px: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                                                        {meta?.label ?? row.name}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                flex: '1 1 0%',
                                                minWidth: 0,
                                                fontSize: 16,
                                                fontWeight: 500,
                                                lineHeight: '24px',
                                                color: 'var(--text-dark)',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {urlText}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: 80,
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <CardToolbarIconButton
                                                kind="edit"
                                                ariaLabel={i18n.t('general_text_edit')}
                                                onClick={() => {
                                                    const currenData = _.get(propaganda, `[${dataIndex}]`, {});
                                                    this.setState({
                                                        propagandaDetail: _.cloneDeep(currenData),
                                                        open: true,
                                                        saveLoading: false,
                                                    });
                                                }}
                                            />
                                            <CardToolbarIconButton
                                                kind="delete"
                                                ariaLabel={i18n.t('general_text_delete')}
                                                onClick={() => {
                                                    const currenData = _.get(
                                                        propaganda,
                                                        `[${dataIndex}]`,
                                                        {} as Propaganda
                                                    );
                                                    if (typeof currenData.id === 'number') {
                                                        this.setState({
                                                            deleteConfirmOpen: true,
                                                            deleteTargetId: currenData.id,
                                                            deleteHighlight: (urlText ?? '').trim() || '—',
                                                            deleteLoading: false,
                                                        });
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>

                <AlertDialog
                    open={deleteConfirmOpen}
                    variant="delete"
                    size="sm"
                    title={i18n.t('first_screen_setting_page_advertise_link')}
                    bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                    highlightText={deleteHighlight}
                    confirmLoading={deleteLoading}
                    onCancel={this.closeDeleteConfirm}
                    onClose={this.closeDeleteConfirm}
                    onConfirm={() => {
                        void this.handleConfirmDeletePropaganda();
                    }}
                />

                <DialogComponent
                    maxWidth="sm"
                    heightSize="small"
                    sx={getDialogFormBodyContentSx('start')}
                    dialogContentStyle={{ paddingTop: 0 }}
                    open={open}
                    onClose={() => {
                        this.setState({ open: false, saveLoading: false });
                    }}
                    defaultHeaderProps={{
                        title: i18n.t('first_screen_setting_page_advertise_info_detail'),
                        onClose: () => {
                            this.setState({ open: false, saveLoading: false });
                        },
                    }}
                    footer={(
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={saveLoading}
                                onClick={() => {
                                    this.setState({ open: false, saveLoading: false });
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <LoadingOverlayButton
                                    color="primary"
                                    variant="contained"
                                    loading={saveLoading}
                                    loadingIndicatorSize={24}
                                    onClick={() => {
                                        void this.savePropaganda();
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </LoadingOverlayButton>
                            </Box>
                        </div>
                    )}
                >
                    <DialogFormContentSurface fullHeight>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                width: '100%',
                            }}
                        >
                            {propagandaForm.map((item) => (
                                <Box key={item.key}>
                                    <BaseInput
                                        required={item.require}
                                        regularType={item.regularType}
                                        variant="outlined"
                                        fullWidth
                                        label={item.label}
                                        value={_.get(propagandaDetail, item.key) ?? ''}
                                        onChange={(event: any) => {
                                            this.onChangePropaganda(event.target.value, item.key);
                                        }}
                                        select={item.select}
                                        items={item.items}
                                        disabled={item.disabled}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </DialogFormContentSurface>
                </DialogComponent>
            </Box>
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};

type State = {
    propaganda: Propaganda[];
    propagandaDetail?: Propaganda;
    open: boolean;
    saveLoading: boolean;
    /** 删除确认弹窗 */
    deleteConfirmOpen: boolean;
    /** 待删除项 id */
    deleteTargetId: number | null;
    /** 待删除项 URL 文案（弹窗高亮） */
    deleteHighlight: string;
    /** 删除接口请求中 */
    deleteLoading: boolean;
};
